import type { Page } from '@hello-aria/makalu-api-client'
import type { UseMutationResult } from '@tanstack/react-query'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { receivablesTableFields } from '@/modules/receivables/enums/receivablesEnums'
import { In } from '@hello-aria/query-string'
import type { ComparisonFilterInput, QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import { isEmpty, isNil } from 'lodash'
import type { Debtor } from '@/shared/models/debtorModels'
import type { Invoice, UpdateAttachments } from '@/shared/models/invoiceModels'
import type { QueryParams } from '@/shared/models/queryModels'
import type {
    CreateReceivable,
    Receivable,
    UpdateReceivable,
    ReceivableGateway
} from '@/shared/models/receivableModels'
import { listDebtors } from '@/shared/services/debtorServices'
import { listInvoices } from '@/shared/services/invoicesServices'
import {
    createReceivable,
    getReceivable,
    listReceivables,
    updateReceivable
} from '@/shared/services/receivableServices'
import { getPageSize, mapQuery } from '@/shared/utils/queryUtils'
import { useMe } from './useMe'
import { useQueryParamsState } from './useQueryParams'
import type { State } from '@/shared/services/uploadServices'
import { updateReceivableAttachments } from '@/shared/services/uploadServices'

export const receivablesQueryKeys = {
    root: ['receivables'],
    deprecated_infiniteReceivables: (
        companyId: string,
        queryParams?: QueryParams
    ): (string | QueryParams | undefined)[] => [...receivablesQueryKeys.root, companyId, queryParams],
    infiniteReceivables: (companyId: string, query?: QueryStringInput): (string | QueryStringInput | undefined)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        query
    ],
    receivableById: (companyId: string, receivableId: string | null | undefined): (undefined | null | string)[] => [
        ...receivablesQueryKeys.root,
        companyId,
        receivableId
    ]
}

type UseInfiniteReceivables = {
    isEnabled?: boolean
    query?: QueryStringInput
}

export const useInfiniteReceivables = (options?: UseInfiniteReceivables) => {
    const { companyId } = useMe()
    const { isEnabled, query } = options ?? {}

    return useInfiniteQuery({
        queryKey: receivablesQueryKeys.infiniteReceivables(companyId, query),

        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            return listReceivables({
                companyId,
                query: {
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    },
                    ...query
                }
            })
        },
        enabled: isEnabled ?? true,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}
interface deprecated_UseInfiniteReceivables {
    isEnabled?: boolean
    customFilters?: ComparisonFilterInput[]
}

export const useDeprecatedInfiniteReceivables = (options?: deprecated_UseInfiniteReceivables) => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()
    const filters = [...(options?.customFilters ?? []), ...(queryParamsState.get('receivables_filter') ?? [])]

    return useInfiniteQuery({
        queryKey: receivablesQueryKeys.deprecated_infiniteReceivables(
            companyId,
            queryParamsState.pick([`receivables_search`, `receivables_sort`, `receivables_filter`])
        ),
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            let relatedInvoices: Page<Invoice> | undefined = undefined
            let relatedDebtors: Page<Debtor> | undefined = undefined
            const receivables = await listReceivables({
                companyId,
                query: mapQuery({
                    filter: !isEmpty(filters)
                        ? {
                              type: 'logical',
                              operator: 'and',
                              filters
                          }
                        : undefined,
                    search: { value: queryParamsState.get('receivables_search'), path: receivablesTableFields },
                    sortBy: queryParamsState.get('receivables_sort'),
                    pagination: { after: pageParam }
                })
            })

            const relatedInvoiceFilters = In(
                'id',
                receivables?.body?.map((receivable) => receivable.invoiceId).filter((id): id is string => Boolean(id))
            )

            const relatedDebtorFilters = In(
                'identifier.value',
                receivables?.body?.map((receivable) => receivable.debtorIdentifier.value).filter(Boolean)
            )

            if (relatedInvoiceFilters && !isEmpty(relatedInvoiceFilters.value)) {
                relatedInvoices = await listInvoices({
                    companyId,
                    hasAttachments: false,
                    query: mapQuery({
                        filter: relatedInvoiceFilters
                    })
                })
            }
            if (relatedDebtorFilters && !isEmpty(relatedDebtorFilters.value)) {
                relatedDebtors = await listDebtors({
                    companyId,
                    query: mapQuery({
                        filter: relatedDebtorFilters
                    })
                })
            }

            const body = receivables?.body?.map((receivable) => {
                const invoice = relatedInvoices?.body?.find((invoice) => invoice.id === receivable.invoiceId)
                const debtor = relatedDebtors?.body?.find((debtor) => debtor.id === receivable.debtorId)

                return {
                    ...receivable,
                    invoice,
                    debtor
                }
            })

            return {
                ...receivables,
                body
            }
        },
        enabled: options?.isEnabled ?? true,
        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },
        initialPageParam: undefined
    })
}

export const useReceivableById = (receivableId: string | null | undefined) => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: receivablesQueryKeys.receivableById(companyId, receivableId),
        queryFn: () => getReceivable(companyId, receivableId),
        enabled: !isNil(receivableId)
    })
}

export const useCreateReceivable = (): UseMutationResult<Receivable | undefined, unknown, CreateReceivable> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => createReceivable(companyId, payload)
    })
}

export const useUpdateReceivable = (): UseMutationResult<Receivable | undefined, unknown, UpdateReceivable> => {
    const { companyId } = useMe()
    return useMutation({
        mutationFn: (payload) => updateReceivable(companyId, payload)
    })
}

export const useUpdateReceivableAttachments = (
    state: State
): UseMutationResult<ReceivableGateway, unknown, UpdateAttachments<Receivable>> => {
    const company = useMe()
    return useMutation({
        mutationFn: (payload) => updateReceivableAttachments(company.companyId, payload, state)
    })
}

export const useInvalidateReceivables = () => {
    const { companyId } = useMe()
    const queryClient = useQueryClient()

    const invalidateReceivables = () => {
        void queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.root })
    }

    const invalidateReceivableById = (receivableId: string) => {
        void queryClient.invalidateQueries({ queryKey: receivablesQueryKeys.receivableById(companyId, receivableId) })
    }

    return {
        invalidateReceivables,
        invalidateReceivableById
    }
}
